import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import apiService, { api } from '../services/api';
import { Plan } from '../types';

const LandingPageStyles = createGlobalStyle`
  .landing-page * { margin: 0; padding: 0; box-sizing: border-box; }

  .landing-page {
    --primary: #00d4aa;
    --primary-dark: #00b894;
    --primary-glow: rgba(0, 212, 170, 0.3);
    --bg-dark: #0a0e17;
    --bg-card: #111827;
    --bg-card-light: #1a2332;
    --text-light: #ffffff;
    --text-muted: #94a3b8;
    --gradient-primary: linear-gradient(135deg, #00d4aa 0%, #00b894 50%, #0891b2 100%);

    font-family: 'Poppins', sans-serif;
    background-color: var(--bg-dark);
    color: var(--text-light);
    line-height: 1.6;
    overflow-x: hidden;
  }

  .landing-page .bg-glow { position: fixed; width: 600px; height: 600px; border-radius: 50%; filter: blur(150px); opacity: 0.15; pointer-events: none; z-index: 0; }
  .landing-page .bg-glow-1 { top: -200px; left: -200px; background: var(--primary); }
  .landing-page .bg-glow-2 { bottom: -200px; right: -200px; background: #0891b2; }
  .landing-page .container { max-width: 1200px; margin: 0 auto; padding: 0 20px; position: relative; z-index: 1; }
  .landing-page header { padding: 20px 0; position: fixed; width: 100%; top: 0; z-index: 100; background: rgba(10, 14, 23, 0.95); backdrop-filter: blur(10px); border-bottom: 1px solid rgba(0, 212, 170, 0.1); }
  .landing-page .header-content { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 15px; }
  .landing-page .logo { display: flex; align-items: center; text-decoration: none; cursor: pointer; }
  .landing-page .logo img { height: 70px; width: auto; }
  .landing-page nav { display: flex; gap: 25px; align-items: center; }
  .landing-page nav a { color: var(--text-muted); text-decoration: none; font-size: 0.95rem; transition: color 0.3s; cursor: pointer; }
  .landing-page nav a:hover { color: var(--primary); }
  .landing-page .header-buttons { display: flex; gap: 12px; align-items: center; }
  .landing-page .cta-header { background: var(--gradient-primary); color: var(--bg-dark); padding: 12px 28px; border-radius: 50px; font-weight: 600; text-decoration: none; transition: all 0.3s ease; box-shadow: 0 4px 20px var(--primary-glow); cursor: pointer; border: none; }
  .landing-page .cta-header:hover { transform: translateY(-2px); box-shadow: 0 6px 30px var(--primary-glow); }
  .landing-page .cta-header-secondary { background: transparent; color: var(--primary); padding: 12px 28px; border-radius: 50px; font-weight: 600; border: 2px solid var(--primary); transition: all 0.3s ease; cursor: pointer; }
  .landing-page .cta-header-secondary:hover { background: var(--primary); color: var(--bg-dark); transform: translateY(-2px); }
  .landing-page .mobile-toggle { display: none; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 12px; border: 1px solid rgba(0, 212, 170, 0.35); background: rgba(0, 212, 170, 0.08); color: var(--text-light); cursor: pointer; }
  .landing-page .mobile-toggle span { display: block; width: 20px; height: 2px; background: currentColor; position: relative; border-radius: 999px; }
  .landing-page .mobile-toggle span::before,
  .landing-page .mobile-toggle span::after { content: ''; position: absolute; left: 0; width: 20px; height: 2px; background: currentColor; border-radius: 999px; }
  .landing-page .mobile-toggle span::before { top: -6px; }
  .landing-page .mobile-toggle span::after { top: 6px; }
  .landing-page .mobile-menu { display: none; width: 100%; max-height: 0; opacity: 0; transform: translateY(-6px); overflow: hidden; transition: max-height 0.3s ease, opacity 0.2s ease, transform 0.2s ease; border-top: 1px solid rgba(0, 212, 170, 0.1); background: rgba(10, 14, 23, 0.97); }
  .landing-page .mobile-menu.open { max-height: 420px; opacity: 1; transform: translateY(0); }
  .landing-page .mobile-menu-inner { display: flex; flex-direction: column; gap: 14px; padding: 16px 20px 20px; }
  .landing-page .mobile-menu a { color: var(--text-muted); font-size: 1rem; }
  .landing-page .mobile-menu a:hover { color: var(--primary); }
  .landing-page .mobile-actions { display: flex; flex-direction: column; gap: 12px; }
  .landing-page .mobile-actions button { width: 100%; }
  .landing-page .hero { padding: 140px 0 80px; min-height: 100vh; display: flex; align-items: center; }
  .landing-page .hero-wrapper { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; }
  .landing-page .hero-badge { display: inline-block; background: rgba(0, 212, 170, 0.1); border: 1px solid var(--primary); color: var(--primary); padding: 8px 20px; border-radius: 50px; font-size: 0.875rem; font-weight: 500; margin-bottom: 25px; animation: pulse 2s infinite; }
  @keyframes pulse { 0%, 100% { box-shadow: 0 0 0 0 var(--primary-glow); } 50% { box-shadow: 0 0 20px 5px var(--primary-glow); } }
  .landing-page .hero h1 { font-size: clamp(2rem, 4vw, 3.2rem); font-weight: 800; margin-bottom: 20px; line-height: 1.15; }
  .landing-page .hero h1 span { background: var(--gradient-primary); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .landing-page .hero-subtitle { font-size: 1.1rem; color: var(--text-muted); margin-bottom: 35px; line-height: 1.7; }
  .landing-page .hero-ctas { display: flex; gap: 15px; flex-wrap: wrap; }
  .landing-page .hero-cta { display: inline-flex; align-items: center; gap: 10px; background: var(--gradient-primary); color: var(--bg-dark); padding: 18px 35px; border-radius: 50px; font-size: 1rem; font-weight: 700; text-decoration: none; transition: all 0.3s ease; box-shadow: 0 10px 40px var(--primary-glow); cursor: pointer; border: none; }
  .landing-page .hero-cta:hover { transform: translateY(-3px) scale(1.02); box-shadow: 0 15px 50px var(--primary-glow); }
  .landing-page .hero-cta-secondary { background: transparent; border: 2px solid var(--primary); color: var(--primary); box-shadow: none; }
  .landing-page .hero-cta-secondary:hover { background: var(--primary); color: var(--bg-dark); }
  .landing-page .social-proof { display: flex; gap: 40px; margin-top: 40px; flex-wrap: wrap; }
  .landing-page .proof-item { text-align: center; }
  .landing-page .proof-number { font-size: 2.2rem; font-weight: 800; background: var(--gradient-primary); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .landing-page .proof-text { font-size: 0.85rem; color: var(--text-muted); }
  .landing-page .hero-mockup { position: relative; perspective: 1000px; }
  .landing-page .mockup-wrapper { background: linear-gradient(145deg, #1a1f2e 0%, #0d1117 100%); border-radius: 16px; padding: 12px 12px 8px 12px; box-shadow: 0 50px 100px rgba(0, 0, 0, 0.6), 0 0 80px var(--primary-glow), inset 0 1px 0 rgba(255,255,255,0.1); transform: rotateY(-5deg) rotateX(2deg); transition: transform 0.5s ease; border: 1px solid rgba(0, 212, 170, 0.15); }
  .landing-page .mockup-wrapper:hover { transform: rotateY(0deg) rotateX(0deg); }
  .landing-page .mockup-browser-bar { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: rgba(0,0,0,0.3); border-radius: 8px 8px 0 0; margin-bottom: 0; }
  .landing-page .mockup-dots { display: flex; gap: 6px; }
  .landing-page .mockup-dot { width: 10px; height: 10px; border-radius: 50%; }
  .landing-page .mockup-dot:nth-child(1) { background: #ff5f57; }
  .landing-page .mockup-dot:nth-child(2) { background: #febc2e; }
  .landing-page .mockup-dot:nth-child(3) { background: #28c840; }
  .landing-page .mockup-url { flex: 1; background: rgba(255,255,255,0.08); border-radius: 4px; padding: 5px 12px; font-size: 0.7rem; color: var(--text-muted); display: flex; align-items: center; gap: 6px; }
  .landing-page .mockup-url svg { width: 12px; height: 12px; color: #28c840; }
  .landing-page .mockup-screen { border-radius: 0 0 8px 8px; overflow: hidden; position: relative; }
  .landing-page .mockup-screen img { width: 100%; height: auto; display: block; }
  .landing-page .mockup-screen::after { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(180deg, transparent 85%, rgba(10,14,23,0.4) 100%); pointer-events: none; }
  .landing-page .mockup-reflection { position: absolute; top: 0; left: 0; right: 0; height: 40%; background: linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 100%); pointer-events: none; border-radius: 0 0 8px 8px; }
  .landing-page .mockup-badge-floating { position: absolute; background: var(--bg-card); border: 1px solid rgba(0, 212, 170, 0.3); border-radius: 12px; padding: 12px 16px; box-shadow: 0 15px 40px rgba(0,0,0,0.4); z-index: 10; animation: float-badge 4s ease-in-out infinite; }
  .landing-page .mockup-badge-1 { top: -20px; right: -30px; }
  .landing-page .mockup-badge-2 { bottom: 30px; left: -40px; animation-delay: 2s; }
  @keyframes float-badge { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
  .landing-page .badge-icon { width: 36px; height: 36px; background: var(--gradient-primary); border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
  .landing-page .badge-icon svg { width: 18px; height: 18px; color: var(--bg-dark); }
  .landing-page .badge-text { font-size: 0.75rem; font-weight: 600; }
  .landing-page .badge-text span { display: block; font-size: 0.65rem; color: var(--text-muted); font-weight: 400; }
  .landing-page section { padding: 100px 0; }
  .landing-page .section-title { text-align: center; margin-bottom: 60px; }
  .landing-page .section-title h2 { font-size: clamp(1.8rem, 4vw, 2.5rem); font-weight: 700; margin-bottom: 15px; }
  .landing-page .section-title h2 span { background: var(--gradient-primary); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .landing-page .section-title p { color: var(--text-muted); font-size: 1.1rem; max-width: 600px; margin: 0 auto; }
  .landing-page .features { background: linear-gradient(180deg, var(--bg-dark) 0%, var(--bg-card) 50%, var(--bg-dark) 100%); }
  .landing-page .features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 30px; }
  .landing-page .feature-card { background: var(--bg-card-light); border-radius: 20px; padding: 35px; border: 1px solid rgba(0, 212, 170, 0.1); transition: all 0.3s ease; }
  .landing-page .feature-card:hover { transform: translateY(-5px); border-color: var(--primary); box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3); }
  .landing-page .feature-icon { width: 60px; height: 60px; background: var(--gradient-primary); border-radius: 15px; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
  .landing-page .feature-icon svg { width: 30px; height: 30px; color: var(--bg-dark); }
  .landing-page .feature-card h3 { font-size: 1.2rem; font-weight: 600; margin-bottom: 12px; }
  .landing-page .feature-card p { color: var(--text-muted); font-size: 0.95rem; }
  .landing-page .benefits-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 30px; }
  .landing-page .benefit-item { display: flex; gap: 20px; align-items: flex-start; background: var(--bg-card); padding: 25px; border-radius: 15px; border: 1px solid rgba(0, 212, 170, 0.1); }
  .landing-page .benefit-number { width: 45px; height: 45px; background: var(--gradient-primary); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-weight: 700; color: var(--bg-dark); flex-shrink: 0; }
  .landing-page .benefit-content h3 { font-size: 1.1rem; margin-bottom: 8px; }
  .landing-page .benefit-content p { color: var(--text-muted); font-size: 0.9rem; }
  .landing-page .pricing { background: linear-gradient(180deg, var(--bg-dark) 0%, var(--bg-card) 50%, var(--bg-dark) 100%); }
  .landing-page .pricing-grid { display: grid; grid-template-columns: repeat(4, minmax(240px, 1fr)); gap: 24px; max-width: 1200px; margin: 0 auto; }
  .landing-page .pricing-card { background: var(--bg-card); border-radius: 25px; padding: 40px 30px; border: 1px solid rgba(0, 212, 170, 0.1); text-align: center; transition: all 0.3s ease; position: relative; }
  .landing-page .pricing-card:hover { transform: translateY(-10px); }
  .landing-page .pricing-card.featured { border: 2px solid var(--primary); box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4), 0 0 60px var(--primary-glow); }
  .landing-page .pricing-badge { position: absolute; top: -15px; left: 50%; transform: translateX(-50%); background: var(--gradient-primary); color: var(--bg-dark); padding: 8px 25px; border-radius: 50px; font-size: 0.8rem; font-weight: 700; }
  .landing-page .pricing-name { font-size: 1.3rem; font-weight: 600; margin-bottom: 10px; }
  .landing-page .pricing-desc { color: var(--text-muted); font-size: 0.9rem; margin-bottom: 25px; }
  .landing-page .pricing-value { margin-bottom: 25px; }
  .landing-page .pricing-currency { font-size: 1.2rem; font-weight: 600; }
  .landing-page .pricing-amount { font-size: 3.5rem; font-weight: 800; background: var(--gradient-primary); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .landing-page .pricing-period { color: var(--text-muted); font-size: 0.9rem; }
  .landing-page .pricing-features { list-style: none; margin-bottom: 30px; text-align: left; }
  .landing-page .pricing-features li { padding: 12px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.05); display: flex; align-items: center; gap: 12px; font-size: 0.9rem; }
  .landing-page .pricing-features li svg { width: 18px; height: 18px; color: var(--primary); flex-shrink: 0; }
  .landing-page .pricing-cta { display: block; width: 100%; background: var(--gradient-primary); color: var(--bg-dark); padding: 16px; border-radius: 50px; font-size: 1rem; font-weight: 700; text-decoration: none; transition: all 0.3s ease; cursor: pointer; border: none; }
  .landing-page .pricing-cta:hover { transform: scale(1.02); box-shadow: 0 10px 30px var(--primary-glow); }
  .landing-page .pricing-cta-secondary { background: transparent; border: 2px solid var(--primary); color: var(--primary); }
  .landing-page .pricing-cta-secondary:hover { background: var(--primary); color: var(--bg-dark); }
  .landing-page .trial-banner { background: var(--bg-card); border-radius: 20px; padding: 40px; text-align: center; margin-top: 50px; border: 1px solid rgba(0, 212, 170, 0.2); }
  .landing-page .trial-banner h3 { font-size: 1.5rem; margin-bottom: 15px; }
  .landing-page .trial-banner p { color: var(--text-muted); margin-bottom: 25px; }
  .landing-page .testimonials-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)); gap: 25px; }
  .landing-page .google-review { background: #ffffff; border-radius: 12px; padding: 24px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); border: 1px solid #e0e0e0; }
  .landing-page .google-review-header { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 16px; }
  .landing-page .google-avatar { width: 44px; height: 44px; border-radius: 50%; overflow: hidden; flex-shrink: 0; }
  .landing-page .google-avatar img { width: 100%; height: 100%; object-fit: cover; }
  .landing-page .google-avatar-letter { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 500; font-size: 1.1rem; color: white; }
  .landing-page .google-user-info { flex: 1; }
  .landing-page .google-user-info h4 { font-size: 0.95rem; font-weight: 500; color: #202124; margin-bottom: 2px; }
  .landing-page .google-user-meta { display: flex; align-items: center; gap: 6px; font-size: 0.8rem; color: #5f6368; }
  .landing-page .google-reviews-count { color: #5f6368; }
  .landing-page .google-logo { width: 24px; height: 24px; }
  .landing-page .google-rating { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
  .landing-page .google-stars { display: flex; gap: 2px; }
  .landing-page .google-stars svg { width: 16px; height: 16px; fill: #fbbc04; }
  .landing-page .google-date { font-size: 0.8rem; color: #5f6368; }
  .landing-page .google-review-text { font-size: 0.9rem; color: #202124; line-height: 1.6; margin-bottom: 12px; }
  .landing-page .google-helpful { display: flex; align-items: center; gap: 16px; padding-top: 12px; border-top: 1px solid #e8eaed; }
  .landing-page .google-helpful span { font-size: 0.75rem; color: #5f6368; }
  .landing-page .google-helpful-btns { display: flex; gap: 8px; }
  .landing-page .google-helpful-btn { background: none; border: 1px solid #dadce0; border-radius: 16px; padding: 6px 14px; font-size: 0.75rem; color: #1a73e8; cursor: pointer; transition: all 0.2s; }
  .landing-page .google-helpful-btn:hover { background: #f1f3f4; }
  .landing-page .google-badge { display: inline-flex; align-items: center; gap: 4px; background: #e8f0fe; color: #1967d2; font-size: 0.7rem; padding: 3px 8px; border-radius: 4px; margin-left: 8px; }
  .landing-page .reviews-header { display: flex; align-items: center; justify-content: center; gap: 20px; margin-bottom: 40px; padding: 20px; background: #fff; border-radius: 12px; max-width: 500px; margin-left: auto; margin-right: auto; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
  .landing-page .reviews-score { text-align: center; }
  .landing-page .reviews-score-number { font-size: 3rem; font-weight: 400; color: #202124; line-height: 1; }
  .landing-page .reviews-score-stars { display: flex; gap: 2px; margin: 8px 0 4px; }
  .landing-page .reviews-score-stars svg { width: 20px; height: 20px; fill: #fbbc04; }
  .landing-page .reviews-score-count { font-size: 0.8rem; color: #5f6368; }
  .landing-page .reviews-bars { flex: 1; max-width: 200px; }
  .landing-page .review-bar { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
  .landing-page .review-bar-label { font-size: 0.75rem; color: #5f6368; width: 12px; }
  .landing-page .review-bar-track { flex: 1; height: 8px; background: #e8eaed; border-radius: 4px; overflow: hidden; }
  .landing-page .review-bar-fill { height: 100%; background: #fbbc04; border-radius: 4px; }
  .landing-page .faq { background: linear-gradient(180deg, var(--bg-dark) 0%, var(--bg-card) 100%); }
  .landing-page .faq-list { max-width: 800px; margin: 0 auto; }
  .landing-page .faq-item { background: var(--bg-card-light); border-radius: 16px; margin-bottom: 15px; border: 1px solid rgba(0, 212, 170, 0.1); overflow: hidden; }
  .landing-page .faq-question { padding: 25px 30px; cursor: pointer; display: flex; justify-content: space-between; align-items: center; font-weight: 600; transition: all 0.3s ease; }
  .landing-page .faq-question:hover { color: var(--primary); }
  .landing-page .faq-question svg { width: 24px; height: 24px; transition: transform 0.3s ease; flex-shrink: 0; }
  .landing-page .faq-item.active .faq-question svg { transform: rotate(180deg); }
  .landing-page .faq-answer { padding: 0 30px 25px; color: var(--text-muted); display: none; }
  .landing-page .faq-item.active .faq-answer { display: block; }
  .landing-page .cta-section { text-align: center; padding: 80px 0; }
  .landing-page .cta-section h2 { font-size: clamp(1.8rem, 4vw, 2.5rem); margin-bottom: 20px; }
  .landing-page .cta-section h2 span { background: var(--gradient-primary); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .landing-page .cta-section p { color: var(--text-muted); margin-bottom: 30px; max-width: 600px; margin-left: auto; margin-right: auto; }
  .landing-page footer { padding: 50px 0 30px; text-align: center; border-top: 1px solid rgba(0, 212, 170, 0.1); }
  .landing-page .footer-logo { margin-bottom: 20px; }
  .landing-page .footer-logo img { height: 80px; width: auto; }
  .landing-page .footer-text { color: var(--text-muted); font-size: 0.9rem; margin-bottom: 15px; }
  .landing-page .footer-links { display: flex; justify-content: center; gap: 30px; margin-bottom: 20px; flex-wrap: wrap; }
  .landing-page .footer-links a { color: var(--text-muted); text-decoration: none; font-size: 0.9rem; transition: color 0.3s ease; }
  .landing-page .footer-links a:hover { color: var(--primary); }
  .landing-page .footer-badges { display: grid; gap: 16px; justify-items: center; margin: 24px 0; }
  .landing-page .footer-badge-group { display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; }
  .landing-page .footer-badge { min-width: 104px; padding: 10px 14px; border-radius: 14px; border: 1px solid rgba(0, 212, 170, 0.18); background: rgba(255,255,255,0.03); color: var(--text-light); font-size: 0.84rem; font-weight: 700; line-height: 1.3; }
  .landing-page .footer-badge span { display: block; margin-top: 4px; color: var(--text-muted); font-size: 0.72rem; font-weight: 500; }
  .landing-page .floating-cta { position: fixed; bottom: 30px; right: 30px; z-index: 99; }
  .landing-page .floating-cta a { display: flex; align-items: center; gap: 10px; background: var(--gradient-primary); color: var(--bg-dark); padding: 15px 25px; border-radius: 50px; font-weight: 700; text-decoration: none; box-shadow: 0 10px 40px var(--primary-glow); cursor: pointer; }
  .landing-page .astro-assistant { position: fixed; right: 24px; bottom: 84px; z-index: 120; display: flex; flex-direction: column; align-items: flex-end; gap: 18px; }
  .landing-page .astro-character-wrap { position: relative; display: flex; justify-content: flex-end; width: 190px; }
  .landing-page .astro-character { width: 138px; height: 188px; border: none; background: transparent; cursor: pointer; position: relative; display: flex; align-items: flex-end; justify-content: center; padding: 0; filter: drop-shadow(0 28px 38px rgba(0,0,0,0.42)); animation: astro-float 4.8s ease-in-out infinite; }
  .landing-page .astro-character:hover { transform: translateY(-6px) rotate(-2deg); }
  .landing-page .astro-character img { width: 138px; height: 188px; object-fit: contain; display: block; }
  .landing-page .astro-character-shadow { position: absolute; bottom: 2px; width: 104px; height: 18px; border-radius: 50%; background: radial-gradient(circle, rgba(0, 212, 170, 0.2) 0%, rgba(10,14,23,0) 72%); filter: blur(8px); }
  .landing-page .astro-wave-badge { position: absolute; top: 18px; left: 0; background: rgba(255,255,255,0.08); color: #fff; border: 1px solid rgba(0, 212, 170, 0.18); border-radius: 18px; padding: 10px 12px; font-size: 0.78rem; line-height: 1.4; max-width: 118px; backdrop-filter: blur(14px); }
  .landing-page .astro-ping { position: absolute; right: 0; top: 0; min-width: 26px; height: 26px; border-radius: 999px; display: flex; align-items: center; justify-content: center; padding: 0 8px; background: linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%); color: #0a0e17; font-size: 0.7rem; font-weight: 800; border: 1px solid rgba(0,0,0,0.08); transform: translate(10px, -8px); }
  @keyframes astro-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
  .landing-page .astro-panel { width: min(390px, calc(100vw - 32px)); background: rgba(11, 18, 32, 0.96); border: 1px solid rgba(0, 212, 170, 0.18); border-radius: 28px; box-shadow: 0 32px 80px rgba(0,0,0,0.5); overflow: hidden; backdrop-filter: blur(18px); }
  .landing-page .astro-header { display: flex; align-items: center; gap: 14px; padding: 18px 18px 16px; background: linear-gradient(135deg, rgba(0,212,170,0.18) 0%, rgba(8,145,178,0.08) 100%); border-bottom: 1px solid rgba(0, 212, 170, 0.12); }
  .landing-page .astro-avatar { width: 54px; height: 54px; border-radius: 18px; background: linear-gradient(145deg, #152238 0%, #09101c 100%); display: flex; align-items: center; justify-content: center; box-shadow: inset 0 1px 0 rgba(255,255,255,0.08); flex-shrink: 0; }
  .landing-page .astro-avatar img { width: 34px; height: 34px; object-fit: contain; display: block; }
  .landing-page .astro-header-copy h3 { font-size: 1rem; font-weight: 700; color: #fff; margin-bottom: 4px; }
  .landing-page .astro-header-copy p { color: #cbd5e1; font-size: 0.85rem; }
  .landing-page .astro-close { margin-left: auto; width: 38px; height: 38px; border-radius: 12px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.04); color: #fff; cursor: pointer; }
  .landing-page .astro-body { padding: 18px; display: flex; flex-direction: column; gap: 14px; max-height: 62vh; overflow: auto; }
  .landing-page .astro-message { max-width: 92%; padding: 14px 16px; border-radius: 18px; font-size: 0.92rem; line-height: 1.6; white-space: pre-line; }
  .landing-page .astro-message.bot { align-self: flex-start; background: rgba(255,255,255,0.05); border: 1px solid rgba(0, 212, 170, 0.12); color: #eff6ff; }
  .landing-page .astro-message.user { align-self: flex-end; background: var(--gradient-primary); color: #071018; font-weight: 600; }
  .landing-page .astro-suggestions { display: flex; flex-wrap: wrap; gap: 10px; }
  .landing-page .astro-chip { border: 1px solid rgba(0, 212, 170, 0.18); background: rgba(255,255,255,0.03); color: #dbeafe; padding: 10px 14px; border-radius: 999px; cursor: pointer; font-size: 0.83rem; }
  .landing-page .astro-chip:hover { background: rgba(0, 212, 170, 0.12); color: #fff; }
  .landing-page .astro-footer { padding: 0 18px 18px; display: flex; flex-direction: column; gap: 12px; }
  .landing-page .astro-input-wrap { display: flex; gap: 10px; }
  .landing-page .astro-input { flex: 1; min-width: 0; border-radius: 18px; border: 1px solid rgba(0, 212, 170, 0.18); background: rgba(255,255,255,0.04); color: #fff; padding: 14px 16px; outline: none; }
  .landing-page .astro-input::placeholder { color: #94a3b8; }
  .landing-page .astro-send { border: none; border-radius: 18px; background: var(--gradient-primary); color: #071018; padding: 0 18px; font-weight: 800; cursor: pointer; box-shadow: 0 10px 30px rgba(0, 212, 170, 0.22); }
  .landing-page .astro-meta { color: #94a3b8; font-size: 0.75rem; line-height: 1.5; }
  @media (max-width: 968px) {
    .landing-page .hero-wrapper { grid-template-columns: 1fr; text-align: center; gap: 40px; }
    .landing-page .hero-ctas { justify-content: center; }
    .landing-page .social-proof { justify-content: center; }
    .landing-page nav { display: none; }
    .landing-page .header-buttons { display: none; }
    .landing-page .mobile-toggle { display: flex; }
    .landing-page .mobile-menu { display: block; }
    .landing-page .mockup-wrapper { transform: none; }
    .landing-page .mockup-badge-floating { display: none; }
    .landing-page .reviews-header { flex-direction: column; gap: 15px; }
    .landing-page .reviews-bars { max-width: 100%; }
    .landing-page .testimonials-grid { grid-template-columns: 1fr; }
    .landing-page .pricing-grid { grid-template-columns: repeat(2, minmax(240px, 1fr)); max-width: 760px; }
  }
  @media (max-width: 768px) {
    .landing-page .header-content { justify-content: center; }
    .landing-page .pricing-grid { grid-template-columns: 1fr; }
    .landing-page .pricing-amount { font-size: 2.8rem; }
    .landing-page .floating-cta { display: none; }
    .landing-page .astro-assistant { right: 16px; left: 16px; bottom: 18px; align-items: stretch; }
    .landing-page .astro-character-wrap { width: 100%; justify-content: flex-end; }
    .landing-page .astro-character { width: 112px; height: 154px; }
    .landing-page .astro-character img { width: 112px; height: 154px; }
    .landing-page .astro-wave-badge { left: auto; right: 86px; top: 10px; max-width: 108px; }
    .landing-page .astro-panel { width: 100%; }
    .landing-page section { padding: 60px 0; }
  }
`;

const Container = styled.div``;

type AssistantMessage = {
  role: 'bot' | 'user';
  text: string;
};

export const LandingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [assistantInput, setAssistantInput] = useState('');
  const [assistantMessages, setAssistantMessages] = useState<AssistantMessage[]>([
    {
      role: 'bot',
      text: 'Olá. Posso tirar dúvidas sobre planos, app, CRM, fidelização, integrações e implantação.',
    },
  ]);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setIsLoadingPlans(true);
      const response = await apiService.getPlans();

      if (response.success && response.data) {
        const activePlans = response.data
          .filter((plan: Plan) => plan.is_active)
          .sort((a: Plan, b: Plan) => a.price - b.price);
        setPlans(activePlans);
      }
      setIsLoadingPlans(false);
    } catch (error) {
      console.error('Error loading plans:', error);
      setIsLoadingPlans(false);
    }
  };

  useEffect(() => {
    // Load Poppins font
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // FAQ toggle functionality
    const handleFaqClick = (e: Event) => {
      const question = (e.target as HTMLElement).closest('.faq-question');
      if (!question) return;

      const item = question.parentElement;
      const isActive = item?.classList.contains('active');

      document.querySelectorAll('.faq-item').forEach(faq => faq.classList.remove('active'));
      if (!isActive && item) item.classList.add('active');
    };

    document.addEventListener('click', handleFaqClick);

    const scrollWithOffset = (target: Element) => {
      const header = document.querySelector('.landing-page header') as HTMLElement | null;
      const offset = (header?.offsetHeight || 0) + 16;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    };

    // Smooth scroll
    const handleAnchorClick = (e: Event) => {
      const anchor = (e.target as HTMLElement).closest('a[href^="#"]');
      if (!anchor) return;

      e.preventDefault();
      const href = anchor.getAttribute('href');
      if (!href) return;

      const target = document.querySelector(href);
      if (target) {
        scrollWithOffset(target);
      }
    };

    document.addEventListener('click', handleAnchorClick);

    // Header background on scroll
    const handleScroll = () => {
      const header = document.querySelector('.landing-page header') as HTMLElement;
      if (header) {
        header.style.background = window.scrollY > 100 ? 'rgba(10, 14, 23, 0.98)' : 'rgba(10, 14, 23, 0.95)';
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('click', handleFaqClick);
      document.removeEventListener('click', handleAnchorClick);
      window.removeEventListener('scroll', handleScroll);
      document.head.removeChild(link);
    };
  }, []);

  const [siteSections, setSiteSections] = useState<any[]>([]);

  const handleRegister = () => {
    navigate('/register');
  };

  const loadSiteContent = async () => {
    try {
      const response = await api.get('/site-content');
      if (response.data?.success) {
        setSiteSections(response.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar conteudo do site:', error);
    }
  };

  useEffect(() => {
    loadSiteContent();
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const target = document.querySelector(location.hash);
    if (!target) return;

    const scrollWithOffset = () => {
      const header = document.querySelector('.landing-page header') as HTMLElement | null;
      const offset = (header?.offsetHeight || 0) + 16;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    };

    const timeouts = [
      window.setTimeout(scrollWithOffset, 0),
      window.setTimeout(scrollWithOffset, 250),
      window.setTimeout(scrollWithOffset, 800),
    ];
    const handleLoad = () => scrollWithOffset();
    window.addEventListener('load', handleLoad);

    return () => {
      timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      window.removeEventListener('load', handleLoad);
    };
  }, [location.hash, isLoadingPlans, siteSections.length]);

  const getSection = (key: string) => siteSections.find(section => section.key === key);
  const getItems = (key: string) => getSection(key)?.items || [];

  const headerSection = getSection('header');
  const heroSection = getSection('hero');
  const featuresSection = getSection('features');
  const benefitsSection = getSection('benefits');
  const reviewsSummarySection = getSection('reviews_summary');
  const faqSection = getSection('faq');
  const trialSection = getSection('trial');
  const ctaSection = getSection('cta');
  const footerSection = getSection('footer');

  const headerNav = headerSection?.meta?.nav || [];
  const headerButtons = headerSection?.meta?.buttons || [];
  const heroMeta = heroSection?.meta || {};
  const heroProof = heroMeta.proof || [];
  const heroBadges = heroMeta.floating_badges || [];
  const reviewsMeta = reviewsSummarySection?.meta || {};
  const reviewsDistribution = reviewsMeta.distribution || [];
  const footerPaymentMethods = footerSection?.meta?.payment_methods || [
    { label: 'Visa' },
    { label: 'Mastercard' },
    { label: 'Elo' },
    { label: 'American Express' },
    { label: 'Hipercard' },
    { label: 'Pix' },
  ];
  const footerTrustBadges = footerSection?.meta?.trust_badges || [
    { label: 'Site blindado', subtitle: 'Monitoramento ativo' },
    { label: 'Compra segura', subtitle: 'Dados protegidos' },
  ];

  const renderFeatureIcon = (icon?: string) => {
    switch (icon) {
      case 'coins':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'gift':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
          </svg>
        );
      case 'smartphone':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'chart':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'message':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'database':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6l4 2" />
          </svg>
        );
    }
  };

  const renderBadgeIcon = (icon?: string) => {
    if (icon === 'trend') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      );
    }
    return renderFeatureIcon('coins');
  };

  const planNames = plans.map((plan) => plan.name).join(', ');
  const cheapestPaidPlan = plans.find((plan) => Number(plan.price) > 0);
  const freePlan = plans.find((plan) => Number(plan.price) === 0);
  const buildAssistantReply = (question: string) => {
    const normalized = question.toLowerCase();

    if (normalized.includes('preço') || normalized.includes('preco') || normalized.includes('plano') || normalized.includes('planos')) {
      const freeText = freePlan ? `Tem opção gratuita: ${freePlan.name}.` : 'Não encontrei plano gratuito configurado agora.';
      const paidText = cheapestPaidPlan
        ? `O primeiro plano pago hoje é ${cheapestPaidPlan.name}, por R$ ${Number(cheapestPaidPlan.price).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}/mês.`
        : 'No momento não identifiquei plano pago carregado na landing.';
      return `${freeText}\n${paidText}\nPlanos publicados: ${planNames || 'nenhum plano carregado ainda'}.\nSe quiser, eu também posso explicar qual se encaixa melhor no seu tipo de operação.`;
    }

    if (normalized.includes('app') || normalized.includes('android') || normalized.includes('ios') || normalized.includes('mobile')) {
      return 'O PontoFiel conta com app mobile para empresa, funcionário e cliente.\nOs planos variam pelos recursos avançados disponíveis dentro da plataforma e do app, não pelo acesso básico ao aplicativo.';
    }

    if (normalized.includes('crm') || normalized.includes('campanha') || normalized.includes('automação') || normalized.includes('automacao')) {
      const crmPlans = plans.filter((plan) => plan.has_crm).map((plan) => plan.name);
      return crmPlans.length
        ? `CRM e campanhas aparecem nos planos: ${crmPlans.join(', ')}.\nNa plataforma isso cobre campanhas, segmentos e automações de relacionamento.`
        : 'No momento não encontrei plano com CRM habilitado na lista atual.';
    }

    if (normalized.includes('ia') || normalized.includes('insight') || normalized.includes('churn') || normalized.includes('clv')) {
      const aiPlans = plans.filter((plan) => plan.has_ai_insights).map((plan) => plan.name);
      return aiPlans.length
        ? `Insights com IA estão disponíveis em: ${aiPlans.join(', ')}.\nEsse módulo cobre leitura de churn, CLV e recomendações para retenção.`
        : 'No momento não encontrei plano com insights de IA habilitados na lista atual.';
    }

    if (normalized.includes('integra') || normalized.includes('api') || normalized.includes('webhook') || normalized.includes('parceiro')) {
      const integrationPlans = plans.filter((plan) => plan.has_partner_network || plan.has_api_access).map((plan) => plan.name);
      return integrationPlans.length
        ? `Integrações e acesso por API aparecem em: ${integrationPlans.join(', ')}.\nIsso costuma cobrir API keys, webhooks e ecossistema de parceiros.`
        : 'Agora eu não encontrei plano com integrações publicadas na listagem atual.';
    }

    if (normalized.includes('cliente') || normalized.includes('fidel') || normalized.includes('ponto') || normalized.includes('recompensa')) {
      return 'O sistema foi desenhado para fidelização com pontuação, resgate de recompensas, regras do programa, CRM e app para cliente, funcionário e empresa.';
    }

    if (normalized.includes('teste') || normalized.includes('grátis') || normalized.includes('gratis') || normalized.includes('começar') || normalized.includes('comecar')) {
      return `Você pode começar pela opção ${freePlan?.name || 'gratuita'}, se ela estiver ativa, ou seguir por "Começar Agora" na landing para criar a conta e evoluir depois.`;
    }

    if (normalized.includes('implant') || normalized.includes('implanta') || normalized.includes('configur') || normalized.includes('suporte')) {
      return 'A implantação costuma passar por cadastro da empresa, configuração das regras, cadastro de recompensas, importação ou criação da base de clientes e ativação dos canais do plano. Se quiser ajuda comercial, o próximo passo ideal é criar a conta ou falar com o time.';
    }

    return 'Posso responder sobre planos, app mobile, CRM, integrações, fidelização, implantação e trial.\nSe a sua pergunta for mais específica, eu respondo com base no que está publicado na landing agora.';
  };

  const submitAssistantQuestion = (question: string) => {
    const trimmed = question.trim();
    if (!trimmed) return;

    const answer = buildAssistantReply(trimmed);
    setAssistantMessages((current) => [
      ...current,
      { role: 'user', text: trimmed },
      { role: 'bot', text: answer },
    ]);
    setAssistantInput('');
    setIsAssistantOpen(true);
  };

  const assistantSuggestions = [
    'Como funciona o app mobile?',
    'Como funciona o CRM?',
    'Tem plano grátis?',
    'Quais planos têm IA e integrações?',
  ];

  return (
    <Container className="landing-page">
      <LandingPageStyles />

      <div className="bg-glow bg-glow-1"></div>
      <div className="bg-glow bg-glow-2"></div>

      <header>
        <div className="container">
          <div className="header-content">
            <div className="logo" onClick={() => navigate('/')}>
              <img src={headerSection?.meta?.logo_url || '/logo1.png'} alt="Ponto Fiel - Sistema de Fidelidade" />
            </div>
            <nav>
              {headerNav.map((item: any, index: number) => {
                const href = item.label.toLowerCase() === 'planos' ? '/#planos' : item.href;
                return <a key={index} href={href}>{item.label}</a>;
              })}
            </nav>
            <button
              className="mobile-toggle"
              aria-label="Abrir menu"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            >
              <span />
            </button>
            <div className="header-buttons">
              {headerButtons.map((button: any, index: number) => {
                if (button.action === 'login') {
                  return (
                    <button key={index} className="cta-header-secondary" onClick={() => navigate('/app')}>
                      {button.label}
                    </button>
                  );
                }
                if (button.action === 'register') {
                  return (
                    <button key={index} className="cta-header" onClick={handleRegister}>
                      {button.label}
                    </button>
                  );
                }
                return (
                  <button key={index} className="cta-header" onClick={() => navigate(button.href || '/register')}>
                    {button.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
            <div className="mobile-menu-inner">
              {headerNav.map((item: any, index: number) => {
                const href = item.label.toLowerCase() === 'planos' ? '/#planos' : item.href;
                return <a key={index} href={href} onClick={() => setIsMobileMenuOpen(false)}>{item.label}</a>;
              })}
              <div className="mobile-actions">
                {headerButtons.map((button: any, index: number) => {
                  if (button.action === 'login') {
                    return (
                      <button
                        key={index}
                        className="cta-header-secondary"
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/app'); }}
                      >
                        {button.label}
                      </button>
                    );
                  }
                  if (button.action === 'register') {
                    return (
                      <button
                        key={index}
                        className="cta-header"
                        onClick={() => { setIsMobileMenuOpen(false); handleRegister(); }}
                      >
                        {button.label}
                      </button>
                    );
                  }
                  return (
                    <button
                      key={index}
                      className="cta-header"
                      onClick={() => { setIsMobileMenuOpen(false); navigate(button.href || '/register'); }}
                    >
                      {button.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="container">
          <div className="hero-wrapper">
            <div className="hero-content">
              {heroMeta.badge && <div className="hero-badge">{heroMeta.badge}</div>}
              <h1>{heroMeta.title || 'Fidelize Clientes e Aumente suas Vendas com Clube de Pontos'}</h1>
              {heroMeta.subtitle && <p className="hero-subtitle">{heroMeta.subtitle}</p>}
              <div className="hero-ctas">
                <button className="hero-cta" onClick={handleRegister}>{heroMeta.cta_primary || 'Começar Agora Grátis'}</button>
                <button className="hero-cta hero-cta-secondary" onClick={() => window.location.hash = '#planos'}>{heroMeta.cta_secondary || 'Ver Planos'}</button>
              </div>
              <div className="social-proof">
                {heroProof.map((proof: any, index: number) => (
                  <div className="proof-item" key={index}>
                    <div className="proof-number">{proof.value}</div>
                    <div className="proof-text">{proof.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hero-mockup">
              {heroBadges[0] && (
                <div className="mockup-badge-floating mockup-badge-1">
                  <div className="badge-icon">
                    {renderBadgeIcon(heroBadges[0].icon)}
                  </div>
                  <div className="badge-text">{heroBadges[0].title}<span>{heroBadges[0].subtitle}</span></div>
                </div>
              )}
              {heroBadges[1] && (
                <div className="mockup-badge-floating mockup-badge-2">
                  <div className="badge-icon">
                    {renderBadgeIcon(heroBadges[1].icon)}
                  </div>
                  <div className="badge-text">{heroBadges[1].title}<span>{heroBadges[1].subtitle}</span></div>
                </div>
              )}
              <div className="mockup-wrapper">
                <div className="mockup-browser-bar">
                  <div className="mockup-dots">
                    <div className="mockup-dot"></div>
                    <div className="mockup-dot"></div>
                    <div className="mockup-dot"></div>
                  </div>
                  <div className="mockup-url">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    {heroMeta.mockup_url || 'app.pontofiel.com.br/dashboard'}
                  </div>
                </div>
                <div className="mockup-screen">
                  <img src={heroMeta.mockup_image || '/pontofieldashborda.png'} alt="Dashboard Ponto Fiel - Sistema de Fidelidade" />
                  <div className="mockup-reflection"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="features" id="funcionalidades">
        <div className="container">
          <div className="section-title">
            <h2>
              {featuresSection?.title || 'Tudo que Você Precisa para'}{' '}
              <span>{featuresSection?.meta?.highlight || 'Fidelizar Clientes'}</span>
            </h2>
            <p>{featuresSection?.subtitle || 'Funcionalidades completas para criar o programa de fidelidade perfeito para seu negócio'}</p>
          </div>
          <div className="features-grid">
            {getItems('features').map((feature: any) => (
              <div key={feature.id} className="feature-card">
                <div className="feature-icon">{renderFeatureIcon(feature.icon)}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="beneficios">
        <div className="container">
          <div className="section-title">
            <h2>
              {benefitsSection?.title || 'Por que Empresas Escolhem o'}{' '}
              <span>{benefitsSection?.meta?.highlight || 'Ponto Fiel'}</span>?
            </h2>
            <p>{benefitsSection?.subtitle || 'Resultados comprovados que transformam seu relacionamento com clientes'}</p>
          </div>
          <div className="benefits-grid">
            {getItems('benefits').map((benefit: any, index: number) => (
              <div key={benefit.id} className="benefit-item">
                <div className="benefit-number">{index + 1}</div>
                <div className="benefit-content">
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing" id="planos">
        <div className="container">
          <div className="section-title">
            <h2>Planos que Cabem no seu <span>Orçamento</span></h2>
            <p>Escolha o plano ideal para evoluir de pontos básicos para fidelização inteligente com operação web e mobile para cliente, empresa e funcionário</p>
          </div>
          {isLoadingPlans ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
              Carregando planos...
            </div>
          ) : (
            <div className="pricing-grid">
              {plans.map((plan, index) => {
                const isFeatured = index === 1; // Plano do meio é o destaque
                const baseFeatures = plan.features || [];
                const dynamicFeatures = [
                  plan.has_crm ? 'CRM habilitado' : null,
                  plan.has_custom_smtp ? 'SMTP personalizado' : null,
                  plan.has_advanced_reports ? 'Relatórios avançados' : null,
                  plan.has_tier_program ? 'Programa de níveis (tiers)' : null,
                  plan.has_customer_automation ? 'Automações por comportamento' : null,
                  plan.has_partner_network ? 'Rede de parceiros earn/burn' : null,
                  plan.has_ai_insights ? 'Insights com IA para retenção' : null,
                  plan.has_priority_support ? 'Suporte prioritário' : null,
                  'App nativo iOS/Android (empresa, funcionário e cliente)',
                ].filter(Boolean) as string[];
                const limitFeatures = [
                  plan.max_stores === -1 ? 'Lojas ilimitadas' : `Até ${plan.max_stores} lojas`,
                  plan.max_employees_per_store === -1 ? 'Funcionários ilimitados' : `Até ${plan.max_employees_per_store} funcionários por loja`,
                  plan.max_customers === -1 ? 'Clientes ilimitados' : `Até ${plan.max_customers.toLocaleString('pt-BR')} clientes`,
                  plan.max_rewards === -1 ? 'Recompensas ilimitadas' : `Até ${plan.max_rewards} recompensas`,
                ];
                const planFeatures = Array.from(new Set([
                  ...limitFeatures,
                  ...baseFeatures,
                  ...dynamicFeatures,
                ]));

                const isFree = Number(plan.price) === 0;

                return (
                  <div key={plan.id} className={`pricing-card ${isFeatured ? 'featured' : ''}`}>
                    {isFeatured && <div className="pricing-badge">MAIS POPULAR</div>}
                    <div className="pricing-name">{plan.name}</div>
                    <div className="pricing-desc">{plan.description || 'Plano ideal para seu negócio'}</div>
                    <div className="pricing-value">
                      {isFree ? (
                        <span className="pricing-amount">Grátis</span>
                      ) : (
                        <>
                          <span className="pricing-currency">R$</span>
                          <span className="pricing-amount">{Number(plan.price).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</span>
                          <span className="pricing-period">/mês</span>
                        </>
                      )}
                    </div>
                    <ul className="pricing-features">
                      {planFeatures.map((feature, idx) => (
                        <li key={idx}>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <button
                      className={`pricing-cta ${!isFeatured ? 'pricing-cta-secondary' : ''}`}
                      onClick={handleRegister}
                    >
                      Começar Agora
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="trial-banner" id="trial">
            <h3>{trialSection?.meta?.title || '🎁 Comece Grátis Agora'}</h3>
            <p>{trialSection?.meta?.text || ''}</p>
            <button className="hero-cta" onClick={handleRegister}>{trialSection?.meta?.button || 'Criar Minha Conta Grátis'}</button>
          </div>
        </div>
      </section>

      <section id="depoimentos">
        <div className="container">
          <div className="section-title">
            <h2>
              {reviewsSummarySection?.title || 'Avaliações dos Nossos'}{' '}
              <span>{reviewsSummarySection?.meta?.highlight || 'Clientes'}</span>
            </h2>
            <p>{reviewsSummarySection?.subtitle || 'Veja o que empresas como a sua falam sobre o Ponto Fiel'}</p>
          </div>

          <div className="reviews-header">
            <div className="reviews-score">
              <div className="reviews-score-number">{reviewsMeta.score || 0}</div>
              <div className="reviews-score-stars">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </div>
              <div className="reviews-score-count">{reviewsMeta.count || 0} avaliações</div>
            </div>
            <div className="reviews-bars">
              {reviewsDistribution.map((bar: any, idx: number) => (
                <div key={idx} className="review-bar">
                  <span className="review-bar-label">{bar.stars}</span>
                  <div className="review-bar-track">
                    <div className="review-bar-fill" style={{ width: `${bar.percent || 0}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="testimonials-grid">
            {getItems('testimonials').map((review: any) => (
              <div key={review.id} className="google-review">
                <div className="google-review-header">
                  <div className="google-avatar-letter" style={{ background: review.meta?.color || '#1a73e8' }}>
                    {review.meta?.initial || review.title?.charAt(0)}
                  </div>
                  <div className="google-user-info">
                    <h4>
                      {review.title}
                      {review.meta?.verified && <span className="google-badge">Proprietário verificado</span>}
                    </h4>
                    <div className="google-user-meta">
                      <span className="google-reviews-count">{review.meta?.reviews || 0} avaliações</span>
                      {review.meta?.guide && <> · <span>Guia local</span></>}
                    </div>
                  </div>
                  <svg className="google-logo" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div className="google-rating">
                  <div className="google-stars">
                    {[...Array(review.meta?.rating || 5)].map((_, i) => (
                      <svg key={i} viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    ))}
                  </div>
                  <span className="google-date">{review.meta?.date || ''}</span>
                </div>
                <p className="google-review-text">{review.description}</p>
                <div className="google-helpful">
                  <span>Esta avaliação foi útil?</span>
                  <div className="google-helpful-btns">
                    <button className="google-helpful-btn">Sim</button>
                    <button className="google-helpful-btn">Não</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="faq" id="faq">
        <div className="container">
          <div className="section-title">
            <h2>
              {faqSection?.title || 'Perguntas'} <span>{faqSection?.meta?.highlight || 'Frequentes'}</span>
            </h2>
            <p>{faqSection?.subtitle || 'Tire suas dúvidas sobre o Ponto Fiel'}</p>
          </div>
          <div className="faq-list">
            {getItems('faq').map((faq: any) => (
              <div key={faq.id} className={`faq-item ${faq.meta?.active ? 'active' : ''}`}>
                <div className="faq-question">
                  <span>{faq.title}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <div className="faq-answer">{faq.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>
            {ctaSection?.meta?.title || 'Pronto para'} <span>{ctaSection?.meta?.highlight || 'Fidelizar'}</span>{' '}
            {ctaSection?.meta?.suffix || 'seus Clientes?'}
          </h2>
          <p>{ctaSection?.meta?.subtitle || 'Junte-se a mais de 2.500 empresas que já aumentaram suas vendas com o Ponto Fiel'}</p>
          <button className="hero-cta" onClick={handleRegister}>{ctaSection?.meta?.button || 'Criar Conta Grátis Agora'}</button>
        </div>
      </section>

      <footer>
        <div className="container">
          <div className="footer-logo">
            <img src={footerSection?.meta?.logo_url || '/logo1.png'} alt="Ponto Fiel" />
          </div>
          <p className="footer-text">{footerSection?.meta?.text || 'O sistema de fidelidade que sua empresa precisa.'}</p>
          <div className="footer-badges">
            <div>
              <p className="footer-text">{footerSection?.meta?.payment_note || 'Pagamentos processados com Mercado Pago.'}</p>
              <div className="footer-badge-group">
                {footerPaymentMethods.map((method: any, index: number) => (
                  <div className="footer-badge" key={`payment-${index}`}>{method.label}</div>
                ))}
              </div>
            </div>
            <div className="footer-badge-group">
              {footerTrustBadges.map((badge: any, index: number) => (
                <div className="footer-badge" key={`trust-${index}`}>
                  {badge.label}
                  {badge.subtitle ? <span>{badge.subtitle}</span> : null}
                </div>
              ))}
            </div>
          </div>
          <div className="footer-links">
            {(footerSection?.meta?.links || []).map((link: any, index: number) => (
              <a key={index} href={link.href}>{link.label}</a>
            ))}
          </div>
          <p className="footer-text">{footerSection?.meta?.copyright || '© 2025 Ponto Fiel. Todos os direitos reservados.'}</p>
        </div>
      </footer>

      <div className="floating-cta">
        <a href="/register" onClick={(event) => { event.preventDefault(); handleRegister(); }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span>Começar Grátis</span>
        </a>
      </div>

      <div className="astro-assistant">
        {isAssistantOpen ? (
          <div className="astro-panel">
            <div className="astro-header">
              <div className="astro-avatar">
                <img src="/astronalta.png" alt="Astronauta assistente" />
              </div>
              <div className="astro-header-copy">
                <h3>Assistente da página</h3>
                <p>Tire dúvidas rápidas sobre o sistema</p>
              </div>
              <button className="astro-close" type="button" onClick={() => setIsAssistantOpen(false)}>
                x
              </button>
            </div>
            <div className="astro-body">
              {assistantMessages.map((message, index) => (
                <div key={`${message.role}-${index}`} className={`astro-message ${message.role}`}>
                  {message.text}
                </div>
              ))}
              <div className="astro-suggestions">
                {assistantSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="astro-chip"
                    onClick={() => submitAssistantQuestion(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
            <div className="astro-footer">
              <div className="astro-input-wrap">
                <input
                  className="astro-input"
                  value={assistantInput}
                  onChange={(event) => setAssistantInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      submitAssistantQuestion(assistantInput);
                    }
                  }}
                  placeholder="Pergunte sobre planos, app, CRM, IA..."
                />
                <button className="astro-send" type="button" onClick={() => submitAssistantQuestion(assistantInput)}>
                  Enviar
                </button>
              </div>
              <div className="astro-meta">
                O assistente responde com base no conteúdo publicado da landing e nos planos carregados no momento.
              </div>
            </div>
          </div>
        ) : null}

        <div className="astro-character-wrap">
          <div className="astro-wave-badge">Tire dúvidas da página</div>
          <button
            type="button"
            className="astro-character"
            onClick={() => setIsAssistantOpen((current) => !current)}
            aria-label="Abrir assistente astronauta"
          >
            <span className="astro-character-shadow" />
            <img src="/astronalta.png" alt="Astronauta assistente" />
            <span className="astro-ping">IA</span>
          </button>
        </div>
      </div>
    </Container>
  );
};
